import React from 'react';
import { graphql } from 'gatsby';
import * as styles from './comparisonchart.module.css';
import { getColor, getIcon, getMediaData } from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { BrandColor, KontentStringValue } from '../../../types/KontentProps';

interface ComparisonChartProps {
    headline: KontentStringValue;
    table_color: BrandColor;
    top_row_color: BrandColor;
    columns: any;
    rows: any;
    kontentItemId?: string;
}

const ComparisonChart: React.FC<ComparisonChartProps> = ({
    headline,
    table_color,
    top_row_color,
    columns,
    rows,
    kontentItemId,
}) => {
    const breakpoints = useBreakpoint();

    const headlineText = headline?.value;
    const tableColor = getColor(table_color);
    const topRowColor = getColor(top_row_color);

    const columnsData = columns.value.map(({ elements }) => ({
        column_name: elements.column_name.value,
        custom_icon: getMediaData(elements.custom_icon)?.url || null,
        custom_icon_width: getMediaData(elements.custom_icon)?.width || 26,
        custom_icon_height: getMediaData(elements.custom_icon)?.height || 19,
        iconType: elements.affirmation_icon.value[0]?.codename || '',
    }));

    const totalColumns = columnsData.length + 1;

    const rowsData = rows.value.map(({ elements }) => ({
        comparisonAspect: elements.comparison_aspect_text.value,
        select_columns_to_mark: elements.select_columns_to_mark.value.map(
            (col) => col.elements.column_name.value
        ),
    }));

    return (
        <div className={styles.wrapper} data-kontent-item-id={kontentItemId}>
            {headlineText && (
                <h2
                    className={styles.headline}
                    data-kontent-element-codename="headline"
                >
                    {headlineText}
                </h2>
            )}
            <div
                className={styles.tableContainer}
                style={
                    breakpoints.xl && totalColumns <= 5
                        ? { padding: '0 10vw' }
                        : {}
                }
            >
                <table
                    className={`${styles.table} ${
                        totalColumns <= 4 ? styles.mobileColumn : ''
                    }`}
                >
                    <thead>
                        <tr>
                            <th
                                // colSpan={2}
                                style={{
                                    backgroundColor:
                                        topRowColor || 'rgb(135, 128, 121)',
                                }}
                            >
                                {' '}
                            </th>
                            {columnsData.map((col, index) => (
                                <th
                                    key={index}
                                    // colSpan={index === 0 ? 2 : undefined}
                                    style={{
                                        backgroundColor:
                                            topRowColor || 'rgb(135, 128, 121)',
                                    }}
                                >
                                    <span
                                        className={styles.columnName}
                                        style={
                                            totalColumns <= 6 && breakpoints.xl
                                                ? { fontSize: '1.2rem' }
                                                : {}
                                        }
                                        data-kontent-element-codename="column_name"
                                    >
                                        {col.column_name}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rowsData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td
                                    style={{
                                        backgroundColor:
                                            tableColor || 'rgb(245, 244, 242)',
                                    }}
                                    data-kontent-element-codename="comparison_aspect_text"
                                >
                                    {row.comparisonAspect}
                                </td>
                                {columnsData.map((col, colIndex) => {
                                    const isMarked =
                                        row.select_columns_to_mark.includes(
                                            col.column_name
                                        );
                                    return (
                                        <td
                                            key={colIndex}
                                            style={{
                                                backgroundColor:
                                                    tableColor ||
                                                    'rgb(245, 244, 242)',
                                            }}
                                        >
                                            {isMarked ? (
                                                col.iconType ===
                                                    'custom_icon' &&
                                                col.custom_icon ? (
                                                    <div
                                                        className={
                                                            styles.customIcon
                                                        }
                                                        data-kontent-element-codename="custom_icon"
                                                    >
                                                        <LazyImage
                                                            src={
                                                                col.custom_icon
                                                            }
                                                            alt={'lotus'}
                                                            width={
                                                                col.custom_icon_width
                                                            }
                                                            height={
                                                                col.custom_icon_height
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: '1.5rem',
                                                        }}
                                                    >
                                                        ✓
                                                    </span>
                                                )
                                            ) : (
                                                <span
                                                    style={{
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    &mdash;
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ComparisonChart;

export const fragmentComparisonChart = graphql`
    fragment ComparisonChart on kontent_item_component___brand_comparison_chart {
        elements {
            headline {
                value
            }
            table_color {
                ...kontentColor
            }
            top_row_color {
                ...kontentColor
            }
            columns {
                value {
                    ... on kontent_item_content_item___column_name___values {
                        elements {
                            column_name {
                                value
                            }
                            custom_icon {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                    width
                                                    height
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            affirmation_icon {
                                value {
                                    codename
                                }
                            }
                        }
                    }
                }
            }
            rows {
                value {
                    ... on kontent_item_content_item___comparison_row {
                        elements {
                            comparison_aspect_text {
                                value
                            }
                            select_columns_to_mark {
                                value {
                                    ... on kontent_item_content_item___column_name___values {
                                        elements {
                                            column_name {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
